import { useCallback, useEffect, useRef, useState } from 'react';
import { getLMNLetter, listClaimDocuments } from '../api/claimsClient';

const TERMINAL = new Set(['generated', 'failed', 'cancelled']);
const POLL_MS = 3000;

/**
 * Poll the existing MedicalNecessityLetter endpoint every 3s. On terminal
 * 'generated', refetch the claim's documents and identify the LMN row by
 * the new lmn_letter_id field on ClaimSupportingDocumentSerializer.
 *
 * @param {string|null} letterId
 * @param {string|null} claimId
 * @param {number} [restartKey] Bump to restart polling on the same letter
 *   after a regenerate (the hook stops on terminal states by default).
 */
export function useLMNLetterPolling(letterId, claimId, restartKey = 0) {
  const [status, setStatus] = useState(/** @type {string|null} */ (null));
  const [letter, setLetter] = useState(/** @type {any} */ (null));
  const [lmnDocument, setLmnDocument] = useState(/** @type {any} */ (null));
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const cancelledRef = useRef(false);
  const intervalRef = useRef(/** @type {ReturnType<typeof setInterval>|null} */ (null));

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setStatus((prev) => (prev && !TERMINAL.has(prev) ? 'cancelled' : prev));
  }, []);

  useEffect(() => {
    if (!letterId) {
      setStatus(null);
      return undefined;
    }
    cancelledRef.current = false;
    setError(null);
    let active = true;

    async function poll() {
      if (cancelledRef.current || !active) return;
      try {
        const fresh = await getLMNLetter(/** @type {string} */ (letterId));
        if (cancelledRef.current || !active) return;
        setLetter(fresh);
        setStatus(fresh.status);
        if (fresh.status === 'failed') {
          setError(fresh.error_message || 'Generation failed.');
        } else {
          setError(null);
        }
        if (TERMINAL.has(fresh.status)) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (fresh.status === 'generated' && claimId) {
            const docs = await listClaimDocuments(claimId);
            if (cancelledRef.current || !active) return;
            // ClaimSupportingDocumentSerializer (claims/serializers/read.py) returns
            // a flat shape with document_type and lmn_letter_id at top level. Prefer
            // matching by lmn_letter_id when available; fall back to flat
            // document_type === 'lmn' for any legacy row that hasn't backfilled the FK.
            const lmn = (docs || [])
              .filter(
                (/** @type {any} */ d) =>
                  d?.lmn_letter_id === letterId || d?.document_type === 'lmn'
              )
              .sort(
                (/** @type {any} */ a, /** @type {any} */ b) =>
                  String(b.attached_at || '').localeCompare(String(a.attached_at || ''))
              )[0];
            if (lmn) setLmnDocument(lmn);
          }
        }
      } catch (_e) {
        // swallow transient — next tick will retry
      }
    }

    poll();
    intervalRef.current = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [letterId, claimId, restartKey]);

  return { status, letter, document: lmnDocument, error, cancel };
}
